import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import DownloadReportButton from '../../../../components/button/DownloadReportButton';
import {
  AssessmentConstants,
  CUSTOM_JOB_MATCH_TYPE,
  CustomFunctionality,
  STANDARD_JOB_MATCH_TYPE,
} from '../../../../shared/Constants';
import { AccountContext } from '../../../../components/AccountContext';
import useCustomFunctionalityCheck from '../../../../hooks/useCustomFunctionalityCheck';
import DownloadReportButtonWithDialog from '../../../../components/button/DownloadReportButtonWithDialog';

const ReportActionContainer = ({ jobMatchType, reportParams, assessmentPrinter }) => {
  const currentAccount = useContext(AccountContext);
  const { doesAccountHaveAccessToCustomFunctionality } = useCustomFunctionalityCheck();

  const { assessment, locale } = reportParams;
  const reportParamsWithoutJobProfile = {
    assessment,
    locale,
    printerFriendly: assessmentPrinter,
    account: currentAccount,
  };

  const reportParamsWithJobProfile = {
    assessment,
    locale,
    printerFriendly: assessmentPrinter,
    account: currentAccount,
    jobProfile: reportParams['job-profile-id'],
  };

  const accountCanSelectMatchPercentageForCareerCounselingReports = doesAccountHaveAccessToCustomFunctionality(
    CustomFunctionality.MINIMUM_MATCH_PERCENTAGE_CC_REPORTS,
  );

  const accountCanGenerateCounselorsInterestsReport = doesAccountHaveAccessToCustomFunctionality(
    CustomFunctionality.COUNSELORS_INTERESTS_REPORT,
  );
  const accountCanGenerateStudentsInterestsReport = doesAccountHaveAccessToCustomFunctionality(
    CustomFunctionality.STUDENTS_INTERESTS_REPORT,
  );
  const accountCanGenerateInterestsGraphReport = doesAccountHaveAccessToCustomFunctionality(
    CustomFunctionality.INTERESTS_GRAPH_REPORT,
  );
  const accountCanGenerateSimplifiedCcReports = doesAccountHaveAccessToCustomFunctionality(
    CustomFunctionality.SIMPLIFIED_CC_REPORTS,
  );

  const accountHasAccessToCustomReports =
    accountCanGenerateCounselorsInterestsReport ||
    accountCanGenerateStudentsInterestsReport ||
    accountCanGenerateInterestsGraphReport ||
    accountCanGenerateSimplifiedCcReports;

  return (
    <>
      <div className="row mt25">
        <DownloadReportButton
          hasToolTip
          buttonClass="btn-full"
          reportParams={reportParamsWithoutJobProfile}
          additionalClass="field field--centered"
          type={AssessmentConstants.REPORT_TYPE.INDIVIDUAL}
          isDisabled={false}
        />
        {accountCanSelectMatchPercentageForCareerCounselingReports ? (
          <DownloadReportButtonWithDialog
            reportParams={reportParamsWithoutJobProfile}
            type={AssessmentConstants.REPORT_TYPE.COUNSELING_INTEREST}
          />
        ) : (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithoutJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.COUNSELING_INTEREST}
            isDisabled={false}
          />
        )}
        {accountCanSelectMatchPercentageForCareerCounselingReports ? (
          <DownloadReportButtonWithDialog
            reportParams={reportParamsWithoutJobProfile}
            type={AssessmentConstants.REPORT_TYPE.COUNSELING_PERSONALITY}
          />
        ) : (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithoutJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.COUNSELING_PERSONALITY}
            isDisabled={false}
          />
        )}
        {jobMatchType === CUSTOM_JOB_MATCH_TYPE && (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.SUMMARY}
            isDisabled={!reportParams['job-profile-id']}
          />
        )}
        {jobMatchType === CUSTOM_JOB_MATCH_TYPE && (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.INTERVIEW}
            isDisabled={!reportParams['job-profile-id']}
          />
        )}
        {jobMatchType === CUSTOM_JOB_MATCH_TYPE && (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.MANAGEMENT}
            isDisabled={!reportParams['job-profile-id']}
          />
        )}

        {jobMatchType === STANDARD_JOB_MATCH_TYPE && (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.SUMMARY_STANDARD_JOB}
            isDisabled={!reportParams['job-profile-id']}
          />
        )}
        {jobMatchType === STANDARD_JOB_MATCH_TYPE && (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.INTERVIEW_STANDARD_JOB}
            isDisabled={!reportParams['job-profile-id']}
          />
        )}
        {jobMatchType === STANDARD_JOB_MATCH_TYPE && (
          <DownloadReportButton
            buttonClass="btn-full"
            reportParams={reportParamsWithJobProfile}
            additionalClass="field field--centered mt5"
            type={AssessmentConstants.REPORT_TYPE.MANAGEMENT_STANDARD_JOB}
            isDisabled={!reportParams['job-profile-id']}
          />
        )}
      </div>
      {accountHasAccessToCustomReports && (
        <div className="row mt25">
          {accountCanGenerateCounselorsInterestsReport && (
            <DownloadReportButton
              buttonClass="btn-full"
              reportParams={reportParamsWithoutJobProfile}
              additionalClass="field field--centered mt5"
              type={AssessmentConstants.REPORT_TYPE.COUNSELORS_INTERESTS_REPORT}
              isDisabled={false}
            />
          )}
          {accountCanGenerateStudentsInterestsReport && (
            <DownloadReportButton
              buttonClass="btn-full"
              reportParams={reportParamsWithoutJobProfile}
              additionalClass="field field--centered mt5"
              type={AssessmentConstants.REPORT_TYPE.STUDENTS_INTERESTS_REPORT}
              isDisabled={false}
            />
          )}
          {accountCanGenerateInterestsGraphReport && (
            <DownloadReportButton
              buttonClass="btn-full"
              reportParams={reportParamsWithoutJobProfile}
              additionalClass="field field--centered mt5"
              type={AssessmentConstants.REPORT_TYPE.INTERESTS_GRAPH_REPORT}
              isDisabled={false}
            />
          )}
          {accountCanGenerateSimplifiedCcReports && (
            <>
              <DownloadReportButton
                buttonClass="btn-full"
                reportParams={reportParamsWithoutJobProfile}
                additionalClass="field field--centered mt5"
                type={AssessmentConstants.REPORT_TYPE.SIMPLIFIED_CC_INTERESTS}
                isDisabled={false}
              />
              <DownloadReportButton
                buttonClass="btn-full"
                reportParams={reportParamsWithoutJobProfile}
                additionalClass="field field--centered mt5"
                type={AssessmentConstants.REPORT_TYPE.SIMPLIFIED_CC_PERSONALITY}
                isDisabled={false}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

ReportActionContainer.propTypes = {
  jobMatchType: PropTypes.string.isRequired,
  reportParams: PropTypes.objectOf(PropTypes.any).isRequired,
  assessmentPrinter: PropTypes.bool,
};

ReportActionContainer.defaultProps = {
  assessmentPrinter: false,
};

export default ReportActionContainer;
